import {
	AgriFranchiseEnum,
	FisheryFranchiseEnum,
	FormError,
	ResidenceEnum,
} from '@/constant';
import {
	convertGenderCodeToName,
	convertNationCodeToName,
	convertProductionTypeCodeToName,
	convertVisaCodeToName,
	formatDate,
	formatNumberKR,
	getDiffExpirationDateString,
} from '@/utils';

import ExcelJS from 'exceljs';
import * as Yup from 'yup';

import {
	TApplicantData,
	TEmployerData,
	TLodgingAddress,
	TValues,
} from './type';

export const initialValues: TValues = {
	programIds: [],
	selectDate: '',
	startDate: '',
	endDate: '',
};

export const convertTypeDetailToLabel = (typeDetail: string) => {
	switch (typeDetail) {
		case AgriFranchiseEnum.AGRICULTURAL_BUSINESS_HOUSEHOLD:
		case AgriFranchiseEnum.AGRICULTURAL_PRODUCT_PROCESSING_COMPANY:
		case FisheryFranchiseEnum.FISHING_OPERATION_HOUSEHOLD:
		case FisheryFranchiseEnum.FISHING_PRODUCT_PROCESSING_COMPANY:
			return '농가';

		case AgriFranchiseEnum.AGRICULTURAL_CORPORATION_ASSOCIATION:
		case FisheryFranchiseEnum.FISHING_CORPORATION_ASSOCIATION:
		case AgriFranchiseEnum.PUBLIC_FRANCHISE:
			return '조합, 법인';
		default:
			return '';
	}
};

export const convertMOULocalGovernmentToName = (value: string) => {
	if (value === ResidenceEnum.E81 || value === ResidenceEnum.C41) {
		return '해당있음';
	}

	return '해당없음';
};

export const covertLodgingAddress = (dataStr: string) => {
	const data: TLodgingAddress[] = JSON.parse(dataStr);
	if (!data) return '';
	return data?.map((e) => e.lodgingAddress).join(', ');
};

const DIVIDE_SCALE = 1000; // Scale factor for display unit (단위: 1,000)

const formatProductionScale = (value: string): string => {
	if (typeof value !== 'string' || value.trim() === '') return '';

	const num = Number(value);
	if (isNaN(num)) return '';

	const scaled = (num / DIVIDE_SCALE).toFixed(4);
	return formatNumberKR(scaled);
};

export const sortEmployerData = (employer: TEmployerData) => {
	const result = {
		//unit
		cityProvince: employer.cityProvince || '',
		countyDistrict: employer.countyDistrict || '',
		typeDetail: convertTypeDetailToLabel(employer.typeDetail) || '',

		//franchise
		employerName: employer.employerName || '',
		employerBirthDate: formatDate(employer.employerBirthDate) || '',
		franchiseName: employer.name || '',
		corporateRegistNo: employer.corporateRegistNo || '',
		address: employer.address || '',
		businessRegistNo: employer.businessRegistNo || '',
		phoneNumber: employer.employerPhone || '',

		//Number of applicants for seasonal worker assignment
		assignmentApplicant: formatNumberKR(employer.assignmentApplicantCnt) || '',
		additionalAssignmentApplicant:
			formatNumberKR(employer.additionalAssignmentApplicantCnt) ||
			(employer.assignmentApplicantCnt ? 0 : ''),
		totalApplicant:
			formatNumberKR(
				employer.assignmentApplicantCnt +
					employer.additionalAssignmentApplicantCnt
			) || '',

		//Crops
		productionType:
			convertProductionTypeCodeToName(employer.productionType) || '',
		productionScale: formatProductionScale(employer.productionScale || ''),

		//History of seasonal worker assignment in the previous year
		hasPrevYearAssignment: employer.hasPrevYearAssignment ? '있음' : '없음',
		prevYearAssignmentCnt: formatNumberKR(employer.prevYearAssignmentCnt) || 0,

		//Previous year compliance items
		penaltyPointsAccumulated: employer.penaltyPointsAccumulated || '',
		isAllocationRestricted: employer.isAllocationRestricted ? 'O' : '-',

		//Seasonal worker accommodation
		hasLodging: employer.hasLodging ? 'O' : 'X',
		lodgingAddress: covertLodgingAddress(employer?.lodgingAddress) || '',
	};

	return result;
};

export const sortApplicantData = (applicant: TApplicantData) => {
	const result = {
		//unit
		cityProvince: applicant.cityProvince || '',
		countyDistrict: applicant.countyDistrict || '',

		//applicant
		name: applicant.applicantFullName || '',
		birthDate: formatDate(applicant.applicantBirthDate) || '',
		gender: convertGenderCodeToName(applicant.gender) || '',
		nationality: convertNationCodeToName(applicant.nationalCode) || '',
		visaCode: convertVisaCodeToName(applicant.visaCode) || '',
		mouLocalGovernment:
			convertMOULocalGovernmentToName(applicant.visaCode) || '',
		entryDate: formatDate(applicant.entryDate) || '',
		expirationDate: formatDate(applicant.expirationDate) || '',
		reEntryCount: applicant.reEntryCount || '',
		unauthorizedDepartureDate:
			formatDate(applicant.unauthorizedDepartureDate) || '',
		departureDate: getDiffExpirationDateString(applicant.departureDate) || '',

		//employer
		typeDetail: convertTypeDetailToLabel(applicant.typeDetail) || '',
		employerName: applicant.employerName || '',
		employerBirthDate: formatDate(applicant.employerBirthDate) || '',
		franchiseName: applicant.franchiseName || '',
		corporateRegistNo: applicant.corporateRegistNo || '',
		address: applicant.address || '',
		businessRegistNo: applicant.businessRegistNo || '',
		employerPhone: applicant.employerPhone || '',

		//Number of seasonal workers assigned
		assignmentApplicant: formatNumberKR(applicant.assignmentApplicantCnt) || '',
		additionalAssignmentApplicant:
			formatNumberKR(applicant.additionalAssignmentApplicantCnt) ||
			(applicant.assignmentApplicantCnt ? 0 : ''),

		totalApplicant:
			formatNumberKR(
				applicant?.assignmentApplicantCnt +
					applicant?.additionalAssignmentApplicantCnt
			) || '',
	};

	return result;
};

export const validationSchema = Yup.object().shape({
	programIds: Yup.array().min(1, FormError.REQUIRED),
});

const getCellFillColor = (key: string) => {
	switch (key) {
		case 'entryDate':
			return 'FFFF99';
		case 'unauthorizedDepartureDate':
			return 'DAEFF3';
		case 'departureDate':
			return 'E5DFEC';

		default:
			return undefined;
	}
};

export const applyCellFillColor = (cell: ExcelJS.Cell, key: string) => {
	const color = getCellFillColor(key);
	if (color) {
		cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
	}
	cell.style = {
		...cell.style,
		border: {
			...cell.style?.border,
			left: { style: 'medium' },
			right: { style: 'medium' },
		},
	};
};

export const addDropdownValidation = (
	cell: ExcelJS.Cell,
	options: string[]
) => {
	cell.dataValidation = {
		type: 'list',
		allowBlank: true,
		formulae: [`"${options.join(',')}"`],
	};
};

export const DROP_DOWN_VALIDATION_OPTIONS_GENDER = ['남', '여'];
export const DROP_DOWN_VALIDATION_OPTIONS_VISA_CODE = [
	'E-8-1',
	'E-8-2',
	'C-4-1',
	'C-4-2',
];
export const DROP_DOWN_VALIDATION_OPTIONS_CROP_TYPE = [
	'①시설원예·특작',
	'②버섯',
	'③과수',
	'④인삼·일반채소',
	'⑤종묘재배',
	'⑥기타원예·특작',
	'⑦곡물',
	'⑧기타식량작물',
	'①㉠해조류(마른김)',
	'①㉡해조류(기타)',
	'①㉢해도류(완도 양식)',
	'②㉠어패류(멸치건조)',
	'②㉡어패류(기타)',
];
